import type { ReactNode } from "react";

import { Button, Heading } from "@stacklet/ui";

import {
  buildCursorInstallLink,
  buildCursorMcpJsonConfig,
  buildMcpJsonConfig,
  buildVsCodeInstallLink,
  buildVsCodeMcpJsonConfig,
  MCP_SERVER_NAME,
  shellQuote,
} from "./installLinks";
import { McpSection } from "./McpSection";
import { McpSnippet } from "./McpSnippet";
import { PersonalApiKeyDisclosure } from "./PersonalApiKeyDisclosure";

/**
 * Every client's anchor id and label, in the order the sections render. The
 * picker at the top of the page iterates this and each section below reads its
 * own entry, so an id can never drift out of sync with the button that jumps
 * to it — a mismatch would be a silent no-op rather than a failure.
 */
export const MCP_CLIENT_SECTIONS = {
  claudeCode: { id: "mcp-claude-code", label: "Claude Code" },
  codex: { id: "mcp-codex", label: "Codex" },
  cursor: { id: "mcp-cursor", label: "Cursor" },
  vsCode: { id: "mcp-vs-code", label: "VS Code" },
  custom: { id: "mcp-custom", label: "Custom" },
} as const;

interface Props {
  /** The deployment's hosted MCP endpoint. */
  url: string;
}

/** Sub-heading within a client's section, e.g. "Manual Install". */
function SectionSubheading({ children }: { children: string }) {
  return (
    <Heading className="text-title-small" level={3}>
      {children}
    </Heading>
  );
}

function Instruction({ children }: { children: ReactNode }) {
  return <p className="text-body-medium text-text-secondary">{children}</p>;
}

/** The endpoint and the `mcp.json` block a manual install needs. */
function ManualInstall({ config, url }: Props & { config: string }) {
  return (
    <>
      <McpSnippet copyLabel="endpoint" value={url} />

      <Instruction>MCP JSON Configuration</Instruction>

      <McpSnippet copyLabel="configuration" value={config} />
    </>
  );
}

function ClaudeCodeSection({ url }: Props) {
  const { id, label } = MCP_CLIENT_SECTIONS.claudeCode;

  return (
    <McpSection id={id} title={label}>
      <Instruction>
        Install Stacklet MCP in Claude Code with a single line:
      </Instruction>

      <McpSnippet
        copyLabel="command"
        value={`claude mcp add --transport http ${MCP_SERVER_NAME} ${shellQuote(url)}`}
      />

      <Instruction>To login to Stacklet MCP:</Instruction>

      <McpSnippet
        copyLabel="command"
        value={`claude mcp login ${MCP_SERVER_NAME}`}
      />

      <PersonalApiKeyDisclosure>
        <McpSnippet
          copyLabel="command"
          value={`claude mcp add --transport http ${MCP_SERVER_NAME} ${shellQuote(url)} --header "Authorization: Bearer <API KEY>"`}
        />
      </PersonalApiKeyDisclosure>
    </McpSection>
  );
}

function CodexSection({ url }: Props) {
  const { id, label } = MCP_CLIENT_SECTIONS.codex;

  return (
    <McpSection id={id} title={label}>
      <Instruction>
        Install Stacklet MCP in Codex with a single line:
      </Instruction>

      <McpSnippet
        copyLabel="command"
        value={`codex mcp add ${MCP_SERVER_NAME} --url ${shellQuote(url)}`}
      />

      <PersonalApiKeyDisclosure>
        <McpSnippet
          copyLabel="command"
          value="export STACKLET_API_KEY=<API KEY>"
        />

        <McpSnippet
          copyLabel="command"
          value={`codex mcp add ${MCP_SERVER_NAME} --url ${shellQuote(url)} --bearer-token-env-var STACKLET_API_KEY`}
        />
      </PersonalApiKeyDisclosure>
    </McpSection>
  );
}

/**
 * Both one-click installs hand the endpoint to a desktop application over a
 * custom protocol. They are buttons rather than links because the console's
 * react-aria router bridge rewrites any href that isn't http(s), which would
 * mangle `cursor://` and `vscode:` into a relative route.
 */
function InstallButton({ href, label }: { href: string; label: string }) {
  return (
    <Button onPress={() => window.location.assign(href)} variant="secondary">
      {label}
    </Button>
  );
}

function CursorSection({ url }: Props) {
  const { id, label } = MCP_CLIENT_SECTIONS.cursor;

  return (
    <McpSection
      action={
        <InstallButton
          href={buildCursorInstallLink(url)}
          label="Add to Cursor"
        />
      }
      id={id}
      title={label}
    >
      <Instruction>
        Click &ldquo;Add to Cursor&rdquo; to integrate Stacklet MCP in Cursor
        automatically.
      </Instruction>

      <SectionSubheading>Manual Install</SectionSubheading>

      <ManualInstall config={buildCursorMcpJsonConfig(url)} url={url} />
    </McpSection>
  );
}

function VsCodeSection({ url }: Props) {
  const { id, label } = MCP_CLIENT_SECTIONS.vsCode;

  return (
    <McpSection
      action={
        <InstallButton
          href={buildVsCodeInstallLink(url)}
          label="Add to VS Code"
        />
      }
      id={id}
      title={label}
    >
      <Instruction>
        Click &ldquo;Add to VS Code&rdquo; to install Stacklet MCP.
      </Instruction>

      <SectionSubheading>Manual Install</SectionSubheading>

      <McpSnippet
        copyLabel="command"
        value={`code --add-mcp ${shellQuote(
          JSON.stringify({ name: MCP_SERVER_NAME, url }),
        )}`}
      />

      <Instruction>MCP JSON Configuration</Instruction>

      <McpSnippet
        copyLabel="configuration"
        value={buildVsCodeMcpJsonConfig(url)}
      />
    </McpSection>
  );
}

function CustomSection({ url }: Props) {
  const { id, label } = MCP_CLIENT_SECTIONS.custom;

  return (
    <McpSection id={id} title={label}>
      <Instruction>For manual MCP configurations in any tool.</Instruction>

      <ManualInstall config={buildMcpJsonConfig(url)} url={url} />
    </McpSection>
  );
}

/** Per-client install instructions, in the order the picker lists them. */
export function McpClientSections({ url }: Props) {
  return (
    <>
      <ClaudeCodeSection url={url} />
      <CodexSection url={url} />
      <CursorSection url={url} />
      <VsCodeSection url={url} />
      <CustomSection url={url} />
    </>
  );
}
